import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageWithLoadingOverlay from '@/components/PageWithLoadingOverlay';
import InvestorOverview from '@/components/pools/list/InvestorOverview';
import PoolList, { PoolListEmptyMessages, PoolListTab } from '@/components/pools/list/PoolList';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { UserValidationInfoSection } from '@/components/UserValidationInfoSection';
import { useModalContext } from '@/contexts/ModalContext';
import { usePoolOverview } from '@/hooks/queries/usePoolQueries';
import { useAuthStore } from '@/stores/authStore';
import { PoolFilters, PoolStatus } from '@/types/pool';
import { isBorrower } from '@/utils/user';
import BorrowerOverview from '../components/pools/list/BorrowerOverview';
//
import { PoolCreationStep, usePoolFormStore } from '../stores/poolFormStore';

const BORROWER_TABS: PoolListTab[] = [
  { id: 'all', label: 'All Pools' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'other', label: 'Other' },
];

const BORROWER_TAB_FILTERS: Record<string, PoolFilters> = {
  active: {
    status: [PoolStatus.FUNDED, PoolStatus.IN_ESCROW, PoolStatus.AWAITING_INVESTORS],
  },
  pending: { status: [PoolStatus.SYSTEM_PENDING] },
  other: {
    status: [PoolStatus.CANCELED, PoolStatus.CLOSED, PoolStatus.REJECTED, PoolStatus.FUNDING_REJECTED],
  },
};

const getBorrowerTabFilters = (tabId: string): PoolFilters => BORROWER_TAB_FILTERS[tabId] || {};

const BORROWER_EMPTY_MESSAGES: PoolListEmptyMessages = {
  all: {
    title: 'No pools yet',
    subtitle: 'Create your first pool to get started with raising capital',
  },
  active: {
    title: 'No active pools',
    subtitle: "You don't have any active pools at the moment",
  },
  pending: {
    title: 'No pending pools',
    subtitle: "You don't have any pending pools at the moment",
  },
  other: {
    title: 'No other pools',
    subtitle: "You don't have any closed, canceled, or rejected pools",
  },
};

const INVESTOR_TABS: PoolListTab[] = [
  { id: 'explore', label: 'Explore Pools' },
  { id: 'investments', label: 'My Investments' },
  { id: 'offers', label: 'My Offers' },
  { id: 'favorites', label: 'Favorites' },
];

const INVESTOR_TAB_FILTERS: Record<string, PoolFilters> = {
  explore: { status: [PoolStatus.AWAITING_INVESTORS] },
  investments: { status: [PoolStatus.FUNDED, PoolStatus.IN_ESCROW] },
  offers: { status: [PoolStatus.OFFER_RECEIVED] },
  favorites: { favorites_only: true },
};

const getInvestorTabFilters = (tabId: string): PoolFilters => INVESTOR_TAB_FILTERS[tabId] || {};

const INVESTOR_EMPTY_MESSAGES: PoolListEmptyMessages = {
  explore: {
    title: 'No pools available',
    subtitle: 'There are no pools available to invest in at the moment',
  },
  investments: {
    title: 'No investments yet',
    subtitle: "You haven't invested in any pools yet",
  },
  offers: {
    title: 'No offers yet',
    subtitle: "You haven't made any offers yet",
  },
};

export default function PoolsPage() {
  const { isAuthenticated, user } = useAuthStore();
  const { openPoolModal, openUserVerificationModal } = useModalContext();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // scroll to top smoothly when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Check if we need to open the verification modal from query parameter
    const shouldOpenModal = searchParams.get('openVerificationModal') === 'true';
    if (shouldOpenModal) {
      openUserVerificationModal();
      // Remove the query parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openVerificationModal');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, openUserVerificationModal, setSearchParams]);

  // Pool creation - using Zustand for form state
  const setCurrentStep = usePoolFormStore((state) => state.setCurrentStep);

  // Fetch overview stats; PoolList fetches its own pools via usePoolsPagination
  const { data: poolOverviewData, isLoading: loadingStats } = usePoolOverview();
  const overviewStats = poolOverviewData?.overviewStats ?? null;

  const handleCreatePoolClick = () => {
    setCurrentStep(PoolCreationStep.POOL_TYPE); // Start with Pool Type selection
    openPoolModal(); // Open modal via context (create mode, no pool passed)
  };

  // Only render if user is authenticated and has a valid role
  if (!isAuthenticated || !user) {
    return null; // This should not happen due to the redirect in useEffect
  }

  return (
    <PageWithLoadingOverlay>
      <div className="min-h-screen bg-white pt-24">
        <UserValidationInfoSection />

        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: 'Pools & Dashboard' }]} />
        </main>

        {/* Main Content - Conditional based on user role */}
        {isBorrower(user) ? (
          <>
            <BorrowerOverview
              overviewStats={overviewStats}
              loading={loadingStats}
              onCreatePoolClick={handleCreatePoolClick}
            />
            <PoolList
              tabs={BORROWER_TABS}
              defaultTab="all"
              getTabFilters={getBorrowerTabFilters}
              emptyMessages={BORROWER_EMPTY_MESSAGES}
            />
          </>
        ) : (
          <>
            <InvestorOverview overviewStats={overviewStats} loading={loadingStats} />
            <PoolList
              tabs={INVESTOR_TABS}
              defaultTab="explore"
              getTabFilters={getInvestorTabFilters}
              emptyMessages={INVESTOR_EMPTY_MESSAGES}
            />
          </>
        )}
      </div>
    </PageWithLoadingOverlay>
  );
}
